import React, { useEffect, useRef, useState } from 'react'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faChevronDown } from '@fortawesome/free-solid-svg-icons'
import './filterSelector.css'

/**
 * FilterSelector - Componente reutilizable de selector
 *
 * @param {string} label - Texto a mostrar en el header cuando hay una opción seleccionada
 * @param {array} options - Array de opciones a mostrar
 * @param {function} getOptionValue - Función que retorna el valor de la opción (ej: item => item.Code)
 * @param {function} getOptionLabel - Función que retorna la etiqueta de la opción (ej: item => item.Name)
 * @param {any} value - Valor seleccionado
 * @param {function} onChange - Callback cuando se selecciona una opción. Recibe el objeto completo
 * @param {boolean} disabled - Desabilitar el selector
 * @param {string} placeholder - Texto cuando no hay selección
 * @param {string} testId
 */
function FilterSelector({
  label,
  options = [],
  getOptionValue,
  getOptionLabel,
  value,
  onChange,
  disabled = false,
  placeholder = 'Seleccionar',
  testId = '',
}) {
  const [isOpen, setIsOpen] = useState(false)
  const containerRef = useRef(null)

  // Validar que las funciones existan
  if (!getOptionValue || !getOptionLabel) {
    console.error(
      'FilterSelector: getOptionValue y getOptionLabel son requeridos'
    )
    return null
  }

  // Cerrar selector al hacer clic fuera
  useEffect(() => {
    function handleClickOutside(event) {
      if (
        containerRef.current &&
        !containerRef.current.contains(event.target)
      ) {
        setIsOpen(false)
      }
    }

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside)
      return () => document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [isOpen])

  if (!getOptionValue || !getOptionLabel) {
    console.error(
      'FilterSelector: getOptionValue y getOptionLabel son requeridos'
    )
    return null
  }

  const handleSelect = option => {
    onChange(option)
    setIsOpen(false)
  }

  const isOptionSelected = option => {
    return getOptionValue(option) === value
  }

  const hasMatchingOption = options.some(
    option => getOptionValue(option) === value
  )
  const isEmpty =
    (value === null || value === undefined || value === '') &&
    !hasMatchingOption

  const matchedOption = options.find(option => getOptionValue(option) === value)
  const optionLabel = matchedOption ? getOptionLabel(matchedOption) : null

  return (
    <div
      id={`select-${testId}`}
      className="filter-selector"
      ref={containerRef}
      data-testid={testId ? `select-${testId}` : undefined}
      onClick={() => !disabled && setIsOpen(!isOpen)}>
      <div className={`filter-selector-header ${disabled ? 'disabled' : ''}`}>
        <span
          className={`filter-label ${
            isEmpty ? 'filter-label-placeholder' : ''
          }`}>
          {!isEmpty ? optionLabel || placeholder : placeholder || label}
        </span>
        <FontAwesomeIcon
          className={`chevron ${isOpen ? 'open' : ''}`}
          icon={faChevronDown}
        />
      </div>

      {isOpen && (
        <ul
          className="filter-selector-list"
          data-testid={testId ? `select-${testId}-options` : undefined}>
          {options && options.length > 0 ? (
            options.map((option, index) => {
              const optionValue = getOptionValue(option)
              const optionLabel = getOptionLabel(option)
              const isSelected = isOptionSelected(option)

              return (
                <li
                  key={optionValue || index}
                  className={`filter-selector-item ${
                    isSelected ? 'selected' : ''
                  }`}
                  data-testid={
                    testId
                      ? `select-${testId}-option-${optionValue}`
                      : undefined
                  }
                  onClick={e => {
                    e.stopPropagation()
                    handleSelect(option)
                  }}>
                  <label htmlFor={`filter-${optionValue}`}>{optionLabel}</label>
                </li>
              )
            })
          ) : (
            <li className="filter-selector-item disabled">
              <span>No hay opciones disponibles</span>
            </li>
          )}
        </ul>
      )}
    </div>
  )
}

export default FilterSelector
